/**
 * マスタの片付け（管理者）— **ドライランを見てから適用**・取り消しできる。
 * 直す内容は `services/masters_cleanup.py`（場所のコード・部署・重複、単位の寄せ）。
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { engApi } from './engApi';
import { jstDateTime } from '@/utils/jstDate';

const KIND_LABEL = {
  loc_deactivate_test: 'テストが作った場所を隠す',
  loc_dept: '場所に部署を入れる',
  loc_recode: '場所のコードを部署付きに',
  loc_merge: '同じ名前の場所を1本に寄せる',
  loc_alias_restore: '消えた別名を戻す',
  loc_dept_link: '部署のひもづけをそろえる',
  loc_rearrange: '場所の並べ替え（入荷棚の共通化・親・コード）',
  uom_add_shared: '単位を共通マスターへ足す',
};

const OP_LABEL = {
  receiving_shared: '共通の入荷棚にする', merge: '寄せる',
  reparent: '親を付け替える', recode: 'コードを直す',
};

const describe = (a) => {
  if (a.kind === 'loc_deactivate_test') return `${a.name}${a.code ? `（${a.code}）` : ''}${a.moves ? `・記録 ${a.moves} 件は残す` : ''}`;
  if (a.kind === 'loc_dept') return `${a.name}${a.code ? `（${a.code}）` : ''} → ${a.label}`;
  if (a.kind === 'loc_alias_restore') return `${a.name}${a.code ? `（${a.code}）` : ''} ← ${(a.add || []).join(' / ') || '—'}`;
  if (a.kind === 'loc_dept_link') return `${a.name}${a.code ? `（${a.code}）` : ''}・${a.dept}${a.to ? '（製造部署にひもづけ）' : '（製造部署のひもづけを外す）'}`;
  if (a.kind === 'loc_rearrange') {
    const head = `${OP_LABEL[a.op] || a.op}: ${a.name}`;
    if (a.op === 'merge') return `${head}（${a.from || '—'} → ${a.to}・記録 ${a.moves || 0} 件を付け替え）`;
    if (a.op === 'reparent') return `${head} → 「${a.to}」の下へ`;
    return `${head}（${a.from || '—'} → ${a.to}・旧コードは別名に残す）`;
  }
  if (a.kind === 'loc_recode') return `${a.name}: ${a.from} → ${a.to}（旧コードは別名に残す）`;
  if (a.kind === 'loc_merge') return `${a.dept || '（部署なし）'} ${a.name}: ${a.from || '—'} → ${a.to || '—'}（記録 ${a.moves} 件を付け替え・負け側は無効化）`;
  return `${a.name}${a.item_count ? `（使用 ${a.item_count} 件）` : ''}`;
};

export default function MasterCleanupDialog({ open, onClose, onApplied }) {
  const [plan, setPlan] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const [p, h] = await Promise.all([engApi.cleanupPlan(), engApi.cleanupHistory()]);
      setPlan(p); setHistory(h || []);
      setPicked(Object.keys(p.counts || {}));
    } catch (e) {
      toast.error(e?.response?.data?.detail || '計画を取得できませんでした');
    } finally { setBusy(false); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    if (busy) return;
    if (!window.confirm('選んだ内容でマスタを直します。あとから「元に戻す」で取り消せます。よろしいですか？')) return;
    setBusy(true);
    try {
      const out = await engApi.cleanupApply(picked);
      const n = Object.entries(out.applied || {}).map(([k, v]) => `${KIND_LABEL[k] || k} ${v}`).join(' / ');
      toast.success(`直しました（${n || '変更なし'}）`);
      await load();
      onApplied?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '適用に失敗しました'); }
    finally { setBusy(false); }
  };

  const undo = async (logId) => {
    if (busy) return;
    if (!window.confirm('この回の片付けを元に戻します。よろしいですか？')) return;
    setBusy(true);
    try {
      await engApi.cleanupUndo(logId);
      toast.success('元に戻しました');
      await load();
      onApplied?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '取り消しに失敗しました'); }
    finally { setBusy(false); }
  };

  const grouped = (plan?.actions || []).reduce((acc, a) => {
    (acc[a.kind] = acc[a.kind] || []).push(a); return acc;
  }, {});
  // 何も変えていない回（押しただけ）は1行にまとめる
  const noOps = history.filter((h) => h.no_op);
  const real = history.filter((h) => !h.no_op);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" aria-describedby={undefined} data-testid="master-cleanup-dialog">
        <DialogHeader><DialogTitle>マスタの片付け（確認）</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500">
          場所のコード・部署・重複と、単位の寄せをまとめて直します。
          <span className="font-semibold text-zinc-700">この画面を開いただけでは1行も変わりません。</span>
          旧コード・旧名称は別名として残り、適用後は「元に戻す」で取り消せます。
        </p>

        <div className="flex flex-wrap gap-2" data-testid="master-cleanup-counts">
          {plan?.nothing_to_do && !busy && (
            <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700"
              data-testid="master-cleanup-clean">片付けるものはありません</span>
          )}
          {Object.entries(plan?.counts || {}).map(([k, v]) => (
            <span key={k} className="rounded border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] text-zinc-600">
              {KIND_LABEL[k] || k}: <span className="font-semibold text-zinc-800">{v}</span>
            </span>
          ))}
        </div>

        <div className="max-h-64 space-y-3 overflow-auto rounded border border-zinc-200 p-2"
          data-testid="master-cleanup-list">
          {Object.entries(grouped).map(([kind, items]) => (
            <div key={kind}>
              <label className="flex items-center gap-1.5 text-[11px] font-semibold text-zinc-700">
                <input type="checkbox" className="h-3.5 w-3.5 accent-teal-600"
                  checked={picked.includes(kind)}
                  onChange={(e) => setPicked((p) => (e.target.checked
                    ? [...p, kind] : p.filter((k) => k !== kind)))}
                  data-testid={`master-cleanup-pick-${kind}`} />
                {KIND_LABEL[kind] || kind}（{items.length}）
              </label>
              {items.map((a, i) => (
                <div key={`${kind}-${i}`} className="text-[11px] leading-5 text-zinc-600"
                  data-testid={`master-cleanup-row-${kind}-${i}`}>
                  ・{describe(a)}
                  {a.blocked && <span className="ml-1 text-amber-700">（{a.note} — 適用しません）</span>}
                </div>
              ))}
            </div>
          ))}
        </div>

        {history.length > 0 && (
          <div className="max-h-36 overflow-auto rounded border border-zinc-200 p-2 text-[11px]"
            data-testid="master-cleanup-history">
            <p className="font-semibold text-zinc-700">これまでの片付け</p>
            {noOps.length > 0 && (
              <div className="py-0.5 text-zinc-400" data-testid="master-cleanup-history-noop">
                変更なし ×{noOps.length}（{(noOps[noOps.length - 1].created_at || '').slice(11, 16)}
                〜{(noOps[0].created_at || '').slice(11, 16)} UTC）— 押しただけでデータは動いていません
              </div>
            )}
            {real.map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-2 py-0.5">
                <span className="text-zinc-600">
                  {jstDateTime(h.created_at)}
                  {' — '}
                  {h.no_op
                    ? <span className="text-zinc-400">変更なし（押しただけ・データは動いていません）</span>
                    : Object.entries(h.counts || {}).map(([k, v]) => `${KIND_LABEL[k] || k} ${v}`).join(' / ')}
                  {h.has_real_merge && (
                    <span className="ml-1 rounded border border-teal-200 bg-teal-50 px-1 text-[10px] font-semibold text-teal-700">
                      実際に統合した回
                    </span>
                  )}
                  {h.undone_at && <span className="ml-1 text-zinc-400">（取り消し済み）</span>}
                </span>
                {!h.undone_at && !h.no_op && (
                  <button onClick={() => undo(h.id)} disabled={busy}
                    className="shrink-0 font-semibold text-teal-700 hover:underline disabled:opacity-40"
                    data-testid={`master-cleanup-undo-${h.id}`}>元に戻す</button>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="master-cleanup-cancel">閉じる</Button>
          <Button onClick={apply} disabled={busy || picked.length === 0
            || !(plan?.actions || []).some((a) => !a.blocked && picked.includes(a.kind))}
            className="bg-teal-700 hover:bg-teal-800" data-testid="master-cleanup-apply">
            {busy ? '処理中…' : '選んだ内容で直す'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
